import React from 'react';
import {
  Briefcase,
  Users,
  Heart,
  TrendingUp,
  MapPin,
  Clock,
} from 'lucide-react';
import MarketingReveal from '../../components/home/MarketingReveal';
import {
  MarketingPage,
  MarketingMediaHero,
  MarketingSection,
  MarketingSplitIntro,
  MarketingFeatureGrid,
  MarketingPrimaryButton,
  MarketingHeroLightButton,
  MarketingGhostOnDark,
} from '../../components/home/MarketingPageChrome';
import { pageValue, sectionOrder } from '../../content/publicPages';
import { SectionLayoutProvider, SectionSlot } from '../../content/sectionTone';
import { usePublicPage } from '../../hooks/usePublicPage';

const CAREERS_HERO =
  'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1800&q=80';

const Careers = () => {
  const { saved, defaults } = usePublicPage('careers');
  const val = (key) => pageValue(saved, {}, defaults, key);
  const order = sectionOrder(saved, 'careers');
  const valueIcons = [Users, TrendingUp, Heart, Briefcase];
  const benefitIcons = [Heart, Clock, TrendingUp, Users];
  const values = (val('values') || []).map((item, index) => ({
    icon: valueIcons[index % valueIcons.length],
    title: item.title,
    description: item.description,
  }));
  const openPositions = val('roles') || [];
  const benefits = (val('benefits') || []).map((item, index) => ({
    icon: benefitIcons[index % benefitIcons.length],
    title: item.title,
    description: item.description,
  }));
  const applyHref = (title) => `/contact?subject=${encodeURIComponent(`Careers: ${title}`)}`;

  return (
    <SectionLayoutProvider order={order} colors={saved.section_colors} hidden={saved.hidden_sections}>
    <MarketingPage>
      <SectionSlot id="hero">
      <MarketingMediaHero
        title={val('title')}
        subtitle={val('subtitle')}
        image={val('image') || CAREERS_HERO}
        actions={
          <>
            <MarketingHeroLightButton href="#open-positions">
              {val('hero_primary_label')}
            </MarketingHeroLightButton>
            <MarketingGhostOnDark to={val('hero_secondary_link') || '/about'}>
              {val('hero_secondary_label')}
            </MarketingGhostOnDark>
          </>
        }
      />
      </SectionSlot>

      <SectionSlot id="intro">
      <MarketingSection tone="white">
        <MarketingSplitIntro title={val('intro_title')}>
          <p>{val('intro_body')}</p>
          <p>{val('intro_body_2')}</p>
        </MarketingSplitIntro>
      </MarketingSection>
      </SectionSlot>

      <SectionSlot id="values">
      <MarketingSection tone="white" className="!pt-0 sm:!pt-0">
        <MarketingFeatureGrid items={values} />
      </MarketingSection>
      </SectionSlot>

      <SectionSlot id="roles">
      <MarketingSection tone="canvas" id="open-positions">
        <div className="mx-auto max-w-3xl">
          <MarketingReveal className="mb-10">
            <h2 className="marketing-section-title mb-3" style={{ color: '#0a0a0a' }}>
              {val('roles_title')}
            </h2>
            <p className="text-[1.05rem] leading-relaxed" style={{ color: '#6b6b6b' }}>
              {val('roles_body')}
            </p>
          </MarketingReveal>

          <div className="divide-y divide-m-border-light border-y border-m-border-light">
            {openPositions.map((role) => (
              <MarketingReveal key={role.title} className="py-8">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <p
                      className="mb-1 text-[11px] font-medium uppercase tracking-[0.14em]"
                      style={{ color: '#8a8a8a' }}
                    >
                      {role.department}
                    </p>
                    <h3 className="mb-2 text-lg font-bold tracking-tight" style={{ color: '#0a0a0a' }}>
                      {role.title}
                    </h3>
                    <p className="mb-3 text-sm leading-relaxed" style={{ color: '#6b6b6b' }}>
                      {role.description}
                    </p>
                    <div className="flex flex-wrap gap-4 text-xs" style={{ color: '#8a8a8a' }}>
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5" />
                        {role.location}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {role.type}
                      </span>
                    </div>
                  </div>
                  <MarketingPrimaryButton to={applyHref(role.title)} className="shrink-0">
                    Apply
                  </MarketingPrimaryButton>
                </div>
              </MarketingReveal>
            ))}
          </div>
        </div>
      </MarketingSection>
      </SectionSlot>

      <SectionSlot id="benefits">
      <MarketingSection tone="white">
        <div className="mx-auto max-w-6xl">
          <MarketingReveal className="mb-10 max-w-xl">
            <h2 className="marketing-section-title" style={{ color: '#0a0a0a' }}>
              {val('benefits_title')}
            </h2>
          </MarketingReveal>
          <MarketingFeatureGrid items={benefits} />
        </div>
      </MarketingSection>
      </SectionSlot>

      <SectionSlot id="closing">
      <MarketingSection tone="charcoal">
        <MarketingReveal className="mx-auto max-w-2xl text-center">
          <h2 className="marketing-section-title mb-3" style={{ color: '#ffffff' }}>
            {val('closing_title')}
          </h2>
          <p className="mb-8 text-[1.05rem] leading-relaxed text-white/70">
            {val('closing_body')}
          </p>
          <MarketingPrimaryButton
            to={val('closing_link') || '/contact?subject=Careers'}
            className="!bg-white !text-m-ink hover:!bg-white/90"
          >
            {val('closing_button')}
          </MarketingPrimaryButton>
        </MarketingReveal>
      </MarketingSection>
      </SectionSlot>
    </MarketingPage>
    </SectionLayoutProvider>
  );
};

export default Careers;
